import React, { useState, useEffect, useContext, useRef } from "react";
import {
  markAnnouncementsRead,
  type Announcement,
  clearAllAnnouncements,
} from "../../services/announcementService";
import { IoClose } from "react-icons/io5";
import "./NotificationsMenu.scss";
import { UserContext } from "components/context/UserContext";
import { useHistory } from "react-router-dom";

interface NotificationsMenuProps {
  isOpen: boolean;
  onClose: () => void;
  refetch: (refetch?: boolean) => Promise<void>;
}

const NotificationsMenu: React.FC<NotificationsMenuProps> = ({
  isOpen,
  onClose,
  refetch,
}) => {
  const { state, dispatch } = useContext(UserContext)!;
  const history = useHistory();
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const menuRef = useRef<HTMLDivElement>(null);
  const [activeTab, setActiveTab] = useState<"announcements" | "messages">(
    "announcements",
  );
  const { announcements = [], messageNotifications = [] } = state;

  const isNotificationRead = (notification: Announcement): boolean => {
    return notification.isReadByMe === 1;
  };

  const handleClear = async (notificationId: number) => {
    try {
      await clearAllAnnouncements([notificationId]);
      await refetch(true); // updates header Context announcements
      dispatch({ type: "BUMP_ANNOUNCEMENTS_VERSION", payload: {} }); // ✅
    } catch (error) {
      setError("Failed to clear announcements");
      console.error("Error clearing announcements:", error);
    }
  };

  const handleClearAll = async () => {
    try {
      const announcementIds = announcements.map((a) => a.id);
      await clearAllAnnouncements(announcementIds);
      await refetch(true);
      dispatch({ type: "BUMP_ANNOUNCEMENTS_VERSION", payload: {} }); // ✅
    } catch (error) {
      setError("Failed to clear announcements");
      console.error("Error clearing announcements:", error);
    }
  };

  useEffect(() => {
    if (!isOpen) return;
    if (announcements.length === 0) return;

    const unreadIds = announcements
      .filter((a: Announcement) => !isNotificationRead(a))
      .map((a: Announcement) => a.id);

    if (unreadIds.length === 0) return;

    void (async () => {
      try {
        await markAnnouncementsRead(unreadIds);

        // ✅ update header UI instantly
        dispatch({
          type: "UPDATE_ANNOUNCEMENTS_READ_STATUS",
          payload: { announcementIds: unreadIds },
        });

        // ✅ keep server + header aligned (optional but good)
        await refetch(true);

        // ✅ tell AnnouncementsPage to refresh its local list
        dispatch({ type: "BUMP_ANNOUNCEMENTS_VERSION", payload: {} }); // ✅
      } catch (err) {
        setError("Failed to mark announcements as read");
        console.error("Error marking announcements as read:", err);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  const navigateToChat = (chatId: number) => {
    dispatch({
      type: "REMOVE_MESSAGE_NOTIFICATION",
      payload: { chatId: chatId },
    });
    history.push(`/chat/${chatId}`);
  };

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const goToAnnouncementsPage = () => {
    onClose();
    history.push("/announcements");
  };

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        (event.target as Element).closest('[data-notifications-toggle="true"]')
      ) {
        return;
      }

      if (
        menuRef.current &&
        !menuRef.current.contains(event.target as Node) &&
        isOpen
      ) {
        onClose();
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, onClose]);

  return (
    <div ref={menuRef} className="notifications-menu">
      <div className="notifications-header">
        <h3>Notifications</h3>

        <div className="notifications-tabs">
          <button
            className={`tab-btn ${
              activeTab === "announcements" ? "active" : ""
            }`}
            onClick={() => setActiveTab("announcements")}
          >
            Announcements
          </button>
          <button
            className={`tab-btn ${activeTab === "messages" ? "active" : ""}`}
            onClick={() => setActiveTab("messages")}
          >
            Messages
          </button>
        </div>

        {activeTab === "announcements" && (
          <div
            style={{
              display: "flex",
              gap: 8,
              flex: 1,
              alignItems: "center",
              justifyContent: "space-between",
            }}
          >
            {announcements?.length > 0 && (
              <button className="view-all-btn" onClick={handleClearAll}>
                Clear All
              </button>
            )}
            <button className="view-all-btn" onClick={goToAnnouncementsPage}>
              Show All
            </button>
          </div>
        )}
      </div>

      <div className="notifications-list">
        {loading ? (
          <div className="notifications-loading">Loading...</div>
        ) : error ? (
          <div className="notifications-error">{error}</div>
        ) : activeTab === "announcements" ? (
          announcements.length > 0 ? (
            announcements.map((notification) => (
              <div
                key={notification.id}
                className={`notification-item ${
                  !isNotificationRead(notification) ? "unread" : ""
                }`}
                onClick={goToAnnouncementsPage}
                role="button"
                tabIndex={0}
                onKeyDown={(e) => {
                  if (e.key === "Enter" || e.key === " ")
                    goToAnnouncementsPage();
                }}
              >
                <div className="notification-content">
                  <div className="notification-wrapper">
                    <div
                      className="notification-message-clamp"
                      dangerouslySetInnerHTML={{ __html: notification.message }}
                    />
                    <button
                      className="clear-notification-btn"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleClear(notification.id);
                      }}
                      aria-label="Dismiss announcement"
                      title="Dismiss"
                    >
                      <IoClose size={20} />
                    </button>
                  </div>

                  <span className="notification-time">
                    {new Date(notification.time).toLocaleDateString("en-US", {
                      month: "short",
                      day: "numeric",
                      hour: "2-digit",
                      minute: "2-digit",
                    })}
                  </span>
                </div>
              </div>
            ))
          ) : (
            <div className="no-notifications">No new announcements</div>
          )
        ) : messageNotifications.length > 0 ? (
          messageNotifications.map((message) => (
            <div
              key={message.id}
              className="notification-item unread"
              onClick={() => navigateToChat(message?.chatId)}
            >
              <div className="notification-content">
                <div className="notification-sender">{message.senderName}</div>
                <div className="notification-wrapper">
                  <p>{message.message}</p>
                </div>
                <span className="notification-time">
                  {new Date(message.time).toLocaleDateString("en-US", {
                    month: "short",
                    day: "numeric",
                    hour: "2-digit",
                    minute: "2-digit",
                  })}
                </span>
              </div>
            </div>
          ))
        ) : (
          <div className="no-notifications">No new messages</div>
        )}
      </div>
    </div>
  );
};

export default NotificationsMenu;
